import { Box, Chip, Container, Stack, styled, Typography, useTheme } from '@mui/material';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BreadcrumbWithBtnBack from 'src/components/BreadcrumbWithBtnBack';
import LoadingSkeleton from 'src/components/common/LoadingSkeleton';
import StyledWhiteBox from 'src/components/common/StyledWhiteBox';
import { TypeContributeCompany } from 'src/features/CompanyDetail/Common/config';
import GeneralInformation from 'src/features/ContributionDetail/GeneralInformation';
import {
  ClaimCompanyInfor,
  EmailCompany,
  OtherNamesCompany,
  RelatedCorporate,
  WebsiteCompany,
  SocialMediaCompany,
  ContactInformation,
  AddressesCompany,
  ContactPeople,
  SocialMediaPeople,
  ReportWrongOtherContact,
} from 'src/features/ContributionDetail/InformationByType';
import NoteFromAdmin from 'src/features/ContributionDetail/NoteFromAdmin';
import { ContributeTypes, getColorContributionStatus, StatusContribution } from 'src/features/Contributions/config';
import { TypeContributePeople } from 'src/features/PeopleDetail/Common/config';
import { useQueryGetContributionById } from 'src/services/queries/useQueryContribute';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import { convertToStartCase, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(7),
  marginTop: theme.spacing(1),
  textAlign: 'center',
}));

const ContributionDetail = () => {
  const theme = useTheme();
  const { id = '', contributionType = '' } = useParams();
  const { data, isFetching } = useQueryGetContributionById(contributionType as ContributeTypes, id);
  const dataContribution = data?.data;
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      navigate(AuthRouteKeys.Home);
    }
  }, [id]);

  const renderInformationByType = () => {
    switch (dataContribution?.type_of_contribution) {
      case TypeContributeCompany.ClaimCompanyProfile:
        return <ClaimCompanyInfor dataContribution={dataContribution} />;
      case TypeContributeCompany.Email:
        return <EmailCompany dataContribution={dataContribution} />;
      case TypeContributeCompany.WebsiteCompanyProfile:
        return <WebsiteCompany dataContribution={dataContribution} />;
      case TypeContributeCompany.RelatedCorporate:
        return <RelatedCorporate dataContribution={dataContribution} />;
      case TypeContributeCompany.OtherNames:
        return <OtherNamesCompany dataContribution={dataContribution} />;
      case TypeContributeCompany.SocialMedia:
        return <SocialMediaCompany dataContribution={dataContribution} />;
      case TypeContributeCompany.ContactInformation:
        return <ContactInformation dataContribution={dataContribution} />;
      case TypeContributeCompany.AlternativeAddressCompany:
      case TypeContributeCompany.PrimaryAddressCompany:
      case TypeContributeCompany.RegisteredAddressCompany:
        return <AddressesCompany dataContribution={dataContribution} />;
      case TypeContributeCompany.ReportWrongOtherContact:
        return <ReportWrongOtherContact dataContribution={dataContribution} />;
      case TypeContributePeople.ContactPeopleProfile:
        return <ContactPeople dataContribution={dataContribution} />;
      case TypeContributePeople.SocialPeopleCompanyProfile:
        return <SocialMediaPeople dataContribution={dataContribution} />;
      default:
        break;
    }
  };

  return (
    <StyledContainer maxWidth="xl">
      <BreadcrumbWithBtnBack
        listItem={[
          {
            text: 'My Contribution',
          },
          {
            text: 'Contribution Detail',
          },
        ]}
      />
      <Box mt={3.75} />
      {isFetching ? (
        <SkeletonDetail />
      ) : (
        <Stack direction="row" spacing={2}>
          <Stack flex={3}>
            <StyledWhiteBox
              p={theme.spacing(2)}
              borderRadius={`${theme.shape.borderRadius * 2}px ${theme.shape.borderRadius * 2}px 0px 0px`}
              borderBottom={`1px solid ${theme.palette.divider}`}
              alignItems="flex-start"
            >
              <Typography align="left" variant="h6">
                Contribution Data
              </Typography>
              <Box mt={1}>
                <Typography align="left" component="span" variant="subtitle2" color={theme.palette.textTertiary.main}>
                  Created At:{' '}
                </Typography>
                <Typography align="left" component="span" variant="subtitle2" color="secondary.main">
                  {formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, dataContribution?.created_at)}
                </Typography>
                <Box
                  mx={1}
                  component="span"
                  display="inline-block"
                  sx={{
                    width: '6px',
                    height: '6px',
                    backgroundColor: theme.palette.divider,
                    borderRadius: 1000,
                  }}
                />
                <Typography align="left" component="span" variant="subtitle2" color={theme.palette.textTertiary.main}>
                  Status:{' '}
                </Typography>
                <Chip
                  label={convertToStartCase(dataContribution?.status || '')}
                  size="small"
                  color={getColorContributionStatus(dataContribution?.status ?? StatusContribution.Trash)}
                />
              </Box>
            </StyledWhiteBox>
            {dataContribution && (
              <StyledWhiteBox p={theme.spacing(2)} borderRadius={0}>
                <GeneralInformation dataContribution={dataContribution} />
              </StyledWhiteBox>
            )}

            <StyledWhiteBox
              p={theme.spacing(2)}
              borderRadius={0}
              borderBottom={`1px solid ${theme.palette.divider}`}
              borderTop={`1px solid ${theme.palette.divider}`}
            >
              {renderInformationByType()}
            </StyledWhiteBox>

            <StyledWhiteBox
              p={theme.spacing(2)}
              borderRadius={`0px 0px ${theme.shape.borderRadius * 2}px ${theme.shape.borderRadius * 2}px`}
            >
              {!isEmpty(dataContribution?.note) && (
                <>
                  <Typography align="left" variant="h6">
                    Note
                  </Typography>
                  <Box
                    mt={1}
                    p={2}
                    bgcolor={theme.palette.background.paper1}
                    borderRadius={theme.shape.borderRadius / 2}
                  >
                    <Typography align="left" variant="body2" color="text.secondary">
                      {renderTextOrDefault(dataContribution?.note)}
                    </Typography>
                  </Box>
                </>
              )}
            </StyledWhiteBox>
          </Stack>
          <StyledWhiteBox flex={1} p={theme.spacing(2)}>
            <NoteFromAdmin />
          </StyledWhiteBox>
        </Stack>
      )}
    </StyledContainer>
  );
};

export default ContributionDetail;

const SkeletonDetail = () => {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={2}>
      <StyledWhiteBox flex={3} p={theme.spacing(2)}>
        <LoadingSkeleton count={10} height={24} />
      </StyledWhiteBox>
      <StyledWhiteBox flex={1} p={theme.spacing(2)}>
        <LoadingSkeleton count={5} height={24} />
      </StyledWhiteBox>
    </Stack>
  );
};
